/**
 * Video Draft Test Page
 *
 * Admin page for testing video edit draft persistence and preview.
 * Uses the LessonVideoEditor component with draft saving enabled.
 *
 * Tests:
 * 1. Video upload and original video creation
 * 2. Recording voiceovers and paused-video audio
 * 3. Creating annotation snapshots
 * 4. Saving/loading drafts to Firestore
 * 5. Preview playback with VideoEditPreview component
 * 6. Video processing with drafts
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { processVideo } from '../../utils/videoProcessing';
import {
  createOriginalVideo,
  getOriginalVideos,
  saveVideoEditDraft,
  getDrafts,
  deleteDraft,
  deleteOriginalVideo,
  // updateOriginalVideo,
  getOriginalVideoUrl,
  getStorageBasePath,
  getStorageUrl,
} from '../../services/videoEditDraft';
import { LessonPreview } from '../../components/LessonPreview';
import {
  LessonVideoEditor,
  type VideoEditorDraftData,
} from '../../components/LessonVideoEditor';
import { ADMIN_TEST_PLAYER_ID } from '../../services/playerLibrary';
import type { OriginalVideo, VideoEditDraft, VideoContext, VoiceoverNotableSegmentClip, NotableSegment } from '../../types/videoEdit';

// Admin context for isolated testing (no conversation required)
const ADMIN_CONTEXT: VideoContext = { type: 'admin' };

export default function VideoDraftTest() {
  const { currentUser } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  // Video state
  const [originalVideos, setOriginalVideos] = useState<OriginalVideo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<OriginalVideo | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Draft state
  const [drafts, setDrafts] = useState<VideoEditDraft[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<VideoEditDraft | null>(null);
  const [loadedDraftData, setLoadedDraftData] = useState<VideoEditorDraftData | null>(null);

  // View state
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Pre-loaded data for the preview modal — URLs are fetched before the modal
  // opens so LessonPreview never needs to make Firebase Storage requests itself.
  const [previewClips, setPreviewClips] = useState<VoiceoverNotableSegmentClip[]>([]);
  const [previewSegments, setPreviewSegments] = useState<NotableSegment[]>([]);
  const [previewClipUrls, setPreviewClipUrls] = useState<Map<string, string>>(new Map());
  const [previewLoading, setPreviewLoading] = useState(false);

  // Upload form state
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');

  // Processing state
  const [processingDraftId, setProcessingDraftId] = useState<string | null>(null);

  // Load original videos on mount
  useEffect(() => {
    if (currentUser) {
      loadOriginalVideos();
    }
  }, [currentUser]);

  // Load drafts when video is selected
  useEffect(() => {
    if (selectedVideo) {
      loadDrafts();
      loadVideoUrl();
    }
  }, [selectedVideo]);

  async function loadOriginalVideos() {
    try {
      const videos = await getOriginalVideos(ADMIN_CONTEXT);
      setOriginalVideos(videos);
    } catch (error) {
      console.error('Failed to load original videos:', error);
    }
  }

  async function loadDrafts() {
    if (!selectedVideo) return;
    try {
      const draftList = await getDrafts(ADMIN_CONTEXT, selectedVideo.id);
      setDrafts(draftList);
    } catch (error) {
      console.error('Failed to load drafts:', error);
    }
  }

  async function loadVideoUrl() {
    if (!selectedVideo) return;
    try {
      const url = await getOriginalVideoUrl(selectedVideo);
      setVideoUrl(url);
    } catch (error) {
      console.error('Failed to load video URL:', error);
    }
  }

  // Handle video file upload
  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    setUploading(true);
    try {
      // Generate video ID first so we can use context-based storage path
      const videoId = `video_${Date.now()}`;
      const storagePath = `${getStorageBasePath(ADMIN_CONTEXT, videoId)}/original.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, storagePath);

      // Use resumable upload for large files; capture download URL on completion
      let uploadedDownloadUrl = '';
      await new Promise<void>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload progress: ${progress.toFixed(1)}%`);
          },
          (error) => reject(error),
          async () => {
            try {
              uploadedDownloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            } catch (e) {
              reject(e);
            }
          }
        );
      });

      // Get video duration
      const tempVideoUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.src = tempVideoUrl;

      const duration = await new Promise<number>((resolve) => {
        video.onloadedmetadata = () => {
          resolve(video.duration);
          URL.revokeObjectURL(tempVideoUrl);
        };
      });

      // Create original video document (store download URL to avoid 412 on re-load)
      const originalVideo = await createOriginalVideo(
        ADMIN_CONTEXT,
        currentUser.uid,
        currentUser.displayName || 'Test User',
        storagePath,
        duration,
        undefined, // playerMessage
        uploadTitle || file.name,
        uploadDescription,
        undefined, // videoId
        uploadedDownloadUrl || undefined
      );

      setOriginalVideos((prev) => [originalVideo, ...prev]);
      setSelectedVideo(originalVideo);
      if (uploadedDownloadUrl) setVideoUrl(uploadedDownloadUrl);
      setUploadTitle('');
      setUploadDescription('');
      showSuccess('Video uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      showError('Failed to upload video');
    } finally {
      setUploading(false);
    }
  }

  // Handle video deletion
  async function handleDeleteVideo(video: OriginalVideo, e: React.MouseEvent) {
    e.stopPropagation();

    if (!confirm(`Delete "${video.title || video.id}" and all its drafts?`)) {
      return;
    }

    try {
      await deleteOriginalVideo(ADMIN_CONTEXT, video.id);
      setOriginalVideos((prev) => prev.filter((v) => v.id !== video.id));
      if (selectedVideo?.id === video.id) {
        setSelectedVideo(null);
        setDrafts([]);
      }
      showSuccess('Video and drafts deleted');
    } catch (error) {
      console.error('Failed to delete video:', error);
      showError('Failed to delete video');
    }
  }

  // Handle video title/description update (available for future edit functionality)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // async function handleUpdateVideoMetadata(video: OriginalVideo, title: string, description: string) {
  //   try {
  //     await updateOriginalVideo(ADMIN_CONTEXT, video.id, { title, description });
  //     setOriginalVideos((prev) =>
  //       prev.map((v) => (v.id === video.id ? { ...v, title, description } : v))
  //     );
  //     if (selectedVideo?.id === video.id) {
  //       setSelectedVideo({ ...video, title, description });
  //     }
  //     showSuccess('Video updated');
  //   } catch (error) {
  //     console.error('Failed to update video:', error);
  //     showError('Failed to update video');
  //   }
  // }

  // Handle saving draft from editor (auto-save: returns updated data with storage paths)
  async function handleSaveDraft(data: VideoEditorDraftData): Promise<VideoEditorDraftData> {
    if (!currentUser || !selectedVideo) {
      throw new Error('No user or video selected');
    }

    const savedDraft = await saveVideoEditDraft({
      userId: currentUser.uid,
      userName: currentUser.displayName || 'Test User',
      context: ADMIN_CONTEXT,
      originalVideoId: selectedVideo.id,
      audioMuted: data.audioMuted,
      notes: data.notes,
      existingDraftId: selectedDraft?.id,
      notableSegments: data.notableSegments,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips,
      coachNotes: data.coachNotes,
      attachedDrills: data.attachedDrills,
      videoRotation: data.videoRotation,
    });

    // Persist the draft ID so subsequent auto-saves update the same draft
    setSelectedDraft(savedDraft);

    // Reload drafts list in background
    loadDrafts();

    // Merge storage paths from saved clips back into the editor data
    const storedClipMap = new Map(savedDraft.voiceoverNotableSegmentClips.map((c) => [c.id, c]));
    return {
      ...data,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips.map((clip) => {
        const stored = storedClipMap.get(clip.id);
        return stored
          ? { ...clip, videoStoragePath: stored.videoStoragePath, videoDuration: stored.videoDuration }
          : clip;
      }),
    };
  }

  // Handle loading a draft for editing
  function handleLoadDraft(draft: VideoEditDraft) {
    setSelectedDraft(draft);
    setLoadedDraftData({
      notableSegments: draft.notableSegments ?? [],
      voiceoverNotableSegmentClips: draft.voiceoverNotableSegmentClips ?? [],
      notes: draft.notes,
      audioMuted: draft.audioMuted,
      coachNotes: draft.coachNotes,
      videoRotation: draft.videoRotation,
      attachedDrills: draft.attachedDrills ?? [],
    });
    setShowEditor(true);
  }

  // Handle deleting a draft
  async function handleDeleteDraft(draftId: string) {
    if (!selectedVideo) return;

    try {
      await deleteDraft(ADMIN_CONTEXT, selectedVideo.id, draftId);
      await loadDrafts();
      showSuccess('Draft deleted');
    } catch (error) {
      console.error('Failed to delete draft:', error);
      showError('Failed to delete draft');
    }
  }

  // Handle processing a draft
  async function handleProcessDraft(draft: VideoEditDraft) {
    if (!selectedVideo) return;

    setProcessingDraftId(draft.id);
    showInfo('Processing video with draft edits...');

    try {
      // Pass all draft data directly to the video processor
      // Segments contain voiceover audio, annotations are passed separately
      const segments = draft.recordingSegments.map((seg) => ({
        id: seg.id,
        startTime: seg.startTime,
        endTime: seg.endTime,
        audioPath: seg.audioStoragePath,
      }));

      const annotationSnapshots = draft.annotationSnapshots.map((snap) => ({
        id: snap.id,
        timestamp: snap.timestamp,
        endTime: snap.endTime,
        canvasPath: snap.canvasStoragePath,
      }));

      const audioRecordings = draft.audioRecordings.map((rec) => ({
        id: rec.id,
        timestamp: rec.timestamp,
        audioPath: rec.audioStoragePath,
        duration: rec.duration, // pre-measured; used as fallback if cloud-run probe fails
      }));

      const clipMasks = draft.clipMasks.map((mask) => ({
        id: mask.id,
        startTime: mask.startTime,
        endTime: mask.endTime,
      }));

      const notableSegments = draft.notableSegments || [];
      const voiceoverNotableSegmentClips = (draft.voiceoverNotableSegmentClips || [])
        .filter((clip) => clip.videoStoragePath)
        .map((clip) => {
          const segmentLabels = (clip.segmentIds || [])
            .map((id) => notableSegments.find((s) => s.id === id)?.label)
            .filter(Boolean) as string[];
          const label = clip.label || (segmentLabels.length > 0 ? segmentLabels.join(' + ') : 'Clip');
          return {
            id: clip.id,
            insertionPoint: clip.insertionPoint,
            videoPath: clip.videoStoragePath,
            videoDuration: clip.videoDuration,
            label,
          };
        });

      console.log(`Processing: ${segments.length} segments, ${annotationSnapshots.length} annotations, ${audioRecordings.length} audio recordings, ${voiceoverNotableSegmentClips.length} voiceover clips`);
      console.log('Draft annotation snapshots:', draft.annotationSnapshots);
      console.log('Mapped annotation snapshots being sent:', annotationSnapshots);
      console.log('Voiceover notable segment clips being sent:', voiceoverNotableSegmentClips);

      // Call the video processor (Cloud Function in emulator, Cloud Run in production)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (processVideo as (req: any) => Promise<{ videoUrl: string; storagePath: string }>)({
        videoPath: selectedVideo.storagePath,
        segments,
        annotationSnapshots,
        audioRecordings,
        clipMasks,
        voiceoverNotableSegmentClips: voiceoverNotableSegmentClips.length > 0 ? voiceoverNotableSegmentClips : undefined,
        duration: selectedVideo.duration,
        audioMuted: draft.audioMuted,
      });

      console.log('Video processing complete, result:', result);

      // Get download URL - in emulator, videoUrl is the storage path
      // In production, it's a signed URL
      let downloadUrl = result.videoUrl;
      if (!downloadUrl.startsWith('http')) {
        // It's a storage path, get the download URL
        console.log('Getting download URL from storage path:', result.storagePath);
        const fileRef = ref(storage, result.storagePath);
        downloadUrl = await getDownloadURL(fileRef);
        console.log('Got download URL:', downloadUrl);
      }

      // For large files, use direct download link instead of blob
      // This prevents browser memory issues with large files
      console.log('Starting download from:', downloadUrl);

      // Create download link directly (works better for large files)
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `processed_${draft.title || draft.id}.mp4`;
      a.target = '_blank'; // Open in new tab to trigger download
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      showSuccess('Video processed! Download should start automatically.');
    } catch (error) {
      console.error('Failed to process video:', error);
      showError('Failed to process video: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setProcessingDraftId(null);
    }
  }

  // Handle preview — pre-load storage URLs before opening the modal so that
  // LessonPreview never makes Firebase Storage requests itself (which caused
  // CPU spin while the modal was open).
  async function handlePreviewDraft(draft: VideoEditDraft) {
    setPreviewLoading(true);
    setSelectedDraft(draft);
    try {
      const clips: VoiceoverNotableSegmentClip[] = draft.voiceoverNotableSegmentClips.map(
        (c) => ({ ...c })
      );
      const urlEntries = await Promise.all(
        clips
          .filter((c) => c.videoStoragePath)
          .map(async (c) => {
            try {
              const url = await getStorageUrl(c.videoStoragePath!);
              return [c.id, url] as [string, string];
            } catch {
              return null;
            }
          })
      );
      const clipUrls = new Map<string, string>();
      urlEntries.forEach((e) => { if (e) clipUrls.set(e[0], e[1]); });
      setPreviewClips(clips);
      setPreviewSegments(draft.notableSegments ?? []);
      setPreviewClipUrls(clipUrls);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to load preview data:', error);
      showError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }

  // Open editor for new draft
  function handleNewDraft() {
    setSelectedDraft(null);
    setLoadedDraftData(null);
    setShowEditor(true);
  }

  // Format time for display
  function formatTime(seconds: number) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Video Editor</h1>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h2 className="font-semibold text-blue-900 mb-2">Instructions</h2>
        <ol className="list-decimal list-inside text-blue-800 space-y-1 text-sm">
          <li>Upload a video using the file input below</li>
          <li>Click "New Draft" to open the video editor</li>
          <li>Record voiceovers, add annotations, create clip masks</li>
          <li>Click "Save Draft" to persist edits to Firestore</li>
          <li>Close the editor and click "Preview" to test playback</li>
          <li>Click "Process" to generate and download the edited video</li>
        </ol>
        <div className="mt-3 text-xs text-blue-700">
          Storage: <code>videos/admin/&#123;videoId&#125;</code> (Admin context)
        </div>
      </div>

      {/* Video Upload Section */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">1. Upload Video</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Video title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 input-glow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Video description"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 input-glow"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              {uploading ? 'Uploading...' : 'Upload Video'}
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>
            <span className="text-sm text-gray-500">{originalVideos.length} video(s) uploaded</span>
          </div>
        </div>
      </div>

      {/* Video Selection */}
      {originalVideos.length > 0 && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">2. Select Video</h2>
          <div className="grid gap-3">
            {originalVideos.map((video) => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className={`flex items-center justify-between p-4 rounded-lg border transition cursor-pointer ${
                  selectedVideo?.id === video.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex-1">
                  <div className="font-medium">{video.title || video.id}</div>
                  {video.description && (
                    <div className="text-sm text-gray-600">{video.description}</div>
                  )}
                  <div className="text-sm text-gray-500">
                    Duration: {formatTime(video.duration)} • Uploaded:{' '}
                    {new Date(video.createdAt).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={(e) => handleDeleteVideo(video, e)}
                  className="ml-4 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Draft Management */}
      {selectedVideo && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">3. Drafts for Selected Video</h2>
            <button
              onClick={handleNewDraft}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + New Draft
            </button>
          </div>

          {drafts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No drafts yet. Click "New Draft" to start editing.
            </p>
          ) : (
            <div className="space-y-3">
              {drafts.map((draft) => (
                <div key={draft.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <div className="font-medium">{draft.title || draft.id}</div>
                    {draft.description && (
                      <div className="text-sm text-gray-600">{draft.description}</div>
                    )}
                    <div className="text-sm text-gray-500">
                      {draft.voiceoverNotableSegmentClips.length} clip(s) •{' '}
                      {draft.notableSegments?.length ?? 0} segment(s) • Updated:{' '}
                      {new Date(draft.updatedAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoadDraft(draft)}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handlePreviewDraft(draft)}
                      disabled={previewLoading}
                      className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                    >
                      {previewLoading ? 'Loading…' : 'Preview'}
                    </button>
                    <button
                      onClick={() => handleProcessDraft(draft)}
                      disabled={processingDraftId === draft.id}
                      className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                    >
                      {processingDraftId === draft.id ? 'Processing...' : 'Process'}
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(draft.id)}
                      className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && selectedVideo && (
        <LessonVideoEditor
          videoUrl={videoUrl ?? undefined}
          title="Video Draft Editor"
          subtitle={`Editing: ${selectedVideo.title || selectedVideo.id}`}
          onCancel={() => {
            setShowEditor(false);
            setSelectedDraft(null);
            setLoadedDraftData(null);
          }}
          onSaveDraft={handleSaveDraft}
          initialDraft={loadedDraftData || undefined}
          playerId={ADMIN_TEST_PLAYER_ID}
          originalVideoId={selectedVideo.id}
        />
      )}

      {/* Preview Modal */}
      {showPreview && selectedDraft && (
        <div className="fixed inset-0 bg-black/50 z-modal flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-xl font-semibold">Draft Preview</h2>
                {selectedDraft.title && (
                  <p className="text-sm text-gray-600">{selectedDraft.title}</p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setSelectedDraft(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {/* URLs are pre-loaded in handlePreviewDraft — LessonPreview receives
                  Mode 1 props so it never makes Firebase Storage requests itself. */}
              <LessonPreview
                clips={previewClips}
                notableSegments={previewSegments}
                preloadedClipUrls={previewClipUrls}
                autoPlay={false}
                showControls={true}
                onEnded={() => showInfo('Preview ended')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
